/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Graph Viewport Component (DOM + SVG Presentation)
   High-performance creative canvas supporting:
   - Rubber-band multi-selection box
   - Multi-node synchronized drag with unified undo transaction
   - Duplicate (Cmd+D), Copy/Paste (Cmd+C / Cmd+V)
   - Node Bypass toggle (B)
   - Quick node search palette (Tab)
   - Frame / Group visual overlays
   ═══════════════════════════════════════════════════════════════ */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import type { NodeId, PortId, PortType, Position } from '../../domain/types';
import {
  ConnectPortsCommand,
  DeleteNodeCommand,
  DisconnectEdgeCommand,
  MoveMultipleNodesCommand,
  ToggleNodeCollapseCommand,
  BatchCommand,
  BypassNodeCommand,
  PasteFragmentCommand,
  CreateNodeCommand,
  type GraphClipboardFragment,
} from '../../domain/commands';
import { useDomainBridge } from '../../stores/domainBridge';
import { useSelectionStore } from '../../stores/selectionStore';
import { useViewportStore } from '../../stores/viewportStore';
import { useTransientStore } from '../../stores/transientStore';
import { defaultNodeRegistry } from '../../nodes';
import { NodeElement } from './NodeElement';
import { ConnectionWire } from './ConnectionWire';
import { ConnectionDraft } from './ConnectionDraft';
import { getMarqueeNodeIds, getNodePortPosition, getValidSelection } from '../workspaceGeometry';
import styles from './Viewport.module.css';

export function isEditableShortcutTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).closest !== 'function') return false;
  return Boolean((target as Element).closest('input, textarea, select, [contenteditable="true"], [contenteditable=""]'));
}

type PrimaryInteraction =
  | { kind: 'idle' }
  | { kind: 'panning'; pointerId: number; last: Position }
  | { kind: 'marqueeSelecting'; pointerId: number; start: Position; baseSelection: Set<NodeId> }
  | { kind: 'draggingNodes'; pointerId: number; initialMouse: Position; initialPositions: Record<NodeId, Position> }
  | { kind: 'connecting'; pointerId: number };

export const Viewport: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Stores
  const canonical = useDomainBridge((state) => state.domainState.canonical);
  const dispatch = useDomainBridge((state) => state.dispatch);
  const undo = useDomainBridge((state) => state.undo);
  const redo = useDomainBridge((state) => state.redo);

  const {
    selectedNodeIds,
    selectedEdgeIds,
    selectNode,
    selectEdge,
    setSelection,
    clearSelection,
  } = useSelectionStore();

  const {
    x,
    y,
    zoom,
    panBy,
    zoomBy,
    screenToGraph,
  } = useViewportStore();

  const {
    wireDraft,
    startWireDraft,
    updateWireDraft,
    clearWireDraft,
  } = useTransientStore();

  // Port coordinates cache: key = `${nodeId}:${portId}` -> { x, y } in graph space
  const [portCoords, setPortCoords] = useState<Record<string, Position>>({});

  // Panning gesture state
  const [interactionKind, setInteractionKind] = useState<PrimaryInteraction['kind']>('idle');
  const interactionRef = useRef<PrimaryInteraction>({ kind: 'idle' });
  const isSpacePressedRef = useRef(false);

  // Marquee selection state
  const [marquee, setMarquee] = useState<{ start: Position; current: Position } | null>(null);
  const marqueeBaseSelectionRef = useRef<Set<NodeId>>(new Set());
  const [dragPreviewPositions, setDragPreviewPositions] = useState<Record<NodeId, Position>>({});
  const dragPreviewPositionsRef = useRef<Record<NodeId, Position>>({});

  // Internal clipboard fragment for Copy/Paste/Duplicate
  const clipboardRef = useRef<GraphClipboardFragment | null>(null);

  // Quick node search palette state
  const [quickSearch, setQuickSearch] = useState<{
    open: boolean;
    screenX: number;
    screenY: number;
    graphX: number;
    graphY: number;
    filterText: string;
  }>({
    open: false,
    screenX: 0,
    screenY: 0,
    graphX: 0,
    graphY: 0,
    filterText: '',
  });

  const setPrimaryInteraction = useCallback((interaction: PrimaryInteraction) => {
    interactionRef.current = interaction;
    setInteractionKind(interaction.kind);
  }, []);

  const cancelPointerInteraction = useCallback(() => {
    setPrimaryInteraction({ kind: 'idle' });
    setMarquee(null);
    dragPreviewPositionsRef.current = {};
    setDragPreviewPositions({});
    clearWireDraft();
  }, [clearWireDraft, setPrimaryInteraction]);

  // History operations can remove selected entities. Keep editor selection canonical.
  useEffect(() => {
    const valid = getValidSelection(canonical.nodes, canonical.edges, selectedNodeIds, selectedEdgeIds);
    if (valid.nodeIds.length !== selectedNodeIds.size || valid.edgeIds.length !== selectedEdgeIds.size) {
      setSelection(valid.nodeIds, valid.edgeIds);
    }
  }, [canonical.nodes, canonical.edges, selectedNodeIds, selectedEdgeIds, setSelection]);

  // Track keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isInput = isEditableShortcutTarget(e.target);

      // Quick search Tab key
      if (e.key === 'Tab' && !isInput) {
        e.preventDefault();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const graphPos = screenToGraph(rect.left + centerX, rect.top + centerY, rect);

        setQuickSearch({
          open: true,
          screenX: centerX - 140,
          screenY: centerY - 120,
          graphX: Math.round(graphPos.x),
          graphY: Math.round(graphPos.y),
          filterText: '',
        });
        setTimeout(() => searchInputRef.current?.focus(), 50);
        return;
      }

      if (e.key === 'Escape') {
        if (quickSearch.open) setQuickSearch((prev) => ({ ...prev, open: false }));
        cancelPointerInteraction();
        clearSelection();
        return;
      }

      if (isInput) return;

      // History: Cmd/Ctrl+Z, plus Cmd/Ctrl+Shift+Z for redo.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }

      if (e.code === 'Space' && !e.repeat) {
        isSpacePressedRef.current = true;
      }

      // Select all nodes
      if ((e.metaKey || e.ctrlKey) && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        setSelection(Object.keys(canonical.nodes));
        return;
      }

      // Bypass toggle: B
      if (e.key === 'b' || e.key === 'B') {
        if (selectedNodeIds.size > 0) {
          const cmds = Array.from(selectedNodeIds).map((id) => new BypassNodeCommand(id));
          dispatch(cmds.length === 1 ? cmds[0] : new BatchCommand(cmds, 'Toggle Bypass'));
        }
      }

      // Duplicate: Cmd+D / Ctrl+D
      if ((e.metaKey || e.ctrlKey) && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        if (selectedNodeIds.size > 0) {
          const nodes = Array.from(selectedNodeIds).map((id) => canonical.nodes[id]).filter(Boolean);
          const internalEdges = Object.values(canonical.edges).filter(
            (ed) => selectedNodeIds.has(ed.sourceNodeId) && selectedNodeIds.has(ed.targetNodeId)
          );
          const fragment: GraphClipboardFragment = { nodes, internalEdges };
          dispatch(new PasteFragmentCommand(fragment, { x: 40, y: 40 }));
        }
      }

      // Copy: Cmd+C / Ctrl+C
      if ((e.metaKey || e.ctrlKey) && (e.key === 'c' || e.key === 'C')) {
        if (selectedNodeIds.size > 0) {
          const nodes = Array.from(selectedNodeIds).map((id) => canonical.nodes[id]).filter(Boolean);
          const internalEdges = Object.values(canonical.edges).filter(
            (ed) => selectedNodeIds.has(ed.sourceNodeId) && selectedNodeIds.has(ed.targetNodeId)
          );
          clipboardRef.current = { nodes, internalEdges };
        }
      }

      // Paste: Cmd+V / Ctrl+V
      if ((e.metaKey || e.ctrlKey) && (e.key === 'v' || e.key === 'V')) {
        if (clipboardRef.current && clipboardRef.current.nodes.length > 0) {
          dispatch(new PasteFragmentCommand(clipboardRef.current, { x: 40, y: 40 }));
        }
      }

      // Delete key
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const cmds = [];
        for (const nodeId of selectedNodeIds) {
          cmds.push(new DeleteNodeCommand(nodeId));
        }
        for (const edgeId of selectedEdgeIds) {
          cmds.push(new DisconnectEdgeCommand(edgeId));
        }
        if (cmds.length > 0) {
          dispatch(cmds.length === 1 ? cmds[0] : new BatchCommand(cmds, 'Delete Selection'));
          clearSelection();
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        isSpacePressedRef.current = false;
        if (interactionRef.current.kind === 'panning') cancelPointerInteraction();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [selectedNodeIds, selectedEdgeIds, dispatch, undo, redo, clearSelection, setSelection, canonical, screenToGraph, quickSearch.open, cancelPointerInteraction]);

  // Port measurement callback from NodeElement
  const handlePortPinMeasure = useCallback(
    (nodeId: NodeId, portId: PortId, _isOutput: boolean, pinX: number, pinY: number) => {
      const key = `${nodeId}:${portId}`;
      setPortCoords((prev) => {
        if (prev[key]?.x === pinX && prev[key]?.y === pinY) return prev;
        return {
          ...prev,
          [key]: { x: pinX, y: pinY },
        };
      });
    },
    []
  );

  // Wheel zoom handler
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();

    // Trackpad scroll pans naturally. Pinch gestures arrive as ctrl+wheel in Chromium.
    if (!e.ctrlKey && !e.metaKey) {
      panBy(-e.deltaX, -e.deltaY);
      return;
    }

    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    const factor = Math.exp(-e.deltaY * 0.0025);
    zoomBy(factor, cursorX, cursorY);
  };

  // Pointer down on viewport background
  const handlePointerDown = (e: React.PointerEvent) => {
    if (quickSearch.open) {
      setQuickSearch((prev) => ({ ...prev, open: false }));
    }

    if (e.button === 1 || isSpacePressedRef.current) {
      // Pan mode
      setPrimaryInteraction({ kind: 'panning', pointerId: e.pointerId, last: { x: e.clientX, y: e.clientY } });
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }

    if (e.button === 0 && !(e.target as HTMLElement).closest('[data-node-id]')) {
      // Start rubberband marquee selection
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const graphPos = screenToGraph(e.clientX, e.clientY, rect);

      setMarquee({ start: graphPos, current: graphPos });
      marqueeBaseSelectionRef.current = e.shiftKey ? new Set(selectedNodeIds) : new Set();
      setPrimaryInteraction({
        kind: 'marqueeSelecting',
        pointerId: e.pointerId,
        start: graphPos,
        baseSelection: marqueeBaseSelectionRef.current,
      });
      if (!e.shiftKey) clearSelection();
      e.currentTarget.setPointerCapture(e.pointerId);
      clearWireDraft();
    }
  };

  // Node drag start callback from NodeElement
  const handleNodeDragStart = useCallback(
    (nodeId: NodeId, clientX: number, clientY: number, pointerId: number) => {
      const { selectedNodeIds } = useSelectionStore.getState();
      const nodeIdsToMove = selectedNodeIds.has(nodeId)
        ? Array.from(selectedNodeIds)
        : [nodeId];

      const initialPositions: Record<NodeId, Position> = {};
      for (const id of nodeIdsToMove) {
        const n = canonical.nodes[id];
        if (n) {
          initialPositions[id] = { ...n.position };
        }
      }

      setPrimaryInteraction({
        kind: 'draggingNodes',
        pointerId,
        initialMouse: { x: clientX, y: clientY },
        initialPositions,
      });
      dragPreviewPositionsRef.current = initialPositions;
      setDragPreviewPositions(initialPositions);
    },
    [canonical.nodes, setPrimaryInteraction]
  );

  // Wire draft start from output pin
  const handleStartWire = useCallback(
    (nodeId: NodeId, portId: PortId, type: PortType, screenX: number, screenY: number, pointerId: number) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const graphPos = screenToGraph(screenX, screenY, rect);

      startWireDraft({
        sourceNodeId: nodeId,
        sourcePortId: portId,
        sourceType: type,
        startX: graphPos.x,
        startY: graphPos.y,
        currentX: graphPos.x,
        currentY: graphPos.y,
      });
      setPrimaryInteraction({ kind: 'connecting', pointerId });
    },
    [screenToGraph, startWireDraft, setPrimaryInteraction]
  );

  // Wire draft completed onto input pin
  const handleEndWire = useCallback(
    (targetNodeId: NodeId, targetPortId: PortId) => {
      const activeDraft = useTransientStore.getState().wireDraft;
      if (activeDraft) {
        dispatch(
          new ConnectPortsCommand(
            activeDraft.sourceNodeId,
            activeDraft.sourcePortId,
            targetNodeId,
            targetPortId
          )
        );
        clearWireDraft();
        setPrimaryInteraction({ kind: 'idle' });
      }
    },
    [dispatch, clearWireDraft, setPrimaryInteraction]
  );

  // Global Pointer Move on container
  const handlePointerMove = (e: React.PointerEvent) => {
    // 1. Pan mode
    const interaction = interactionRef.current;
    if (interaction.kind === 'panning') {
      const dx = e.clientX - interaction.last.x;
      const dy = e.clientY - interaction.last.y;
      interaction.last = { x: e.clientX, y: e.clientY };
      panBy(dx, dy);
      return;
    }

    // 2. Wire draft update
    if (interaction.kind === 'connecting' && wireDraft && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const graphPos = screenToGraph(e.clientX, e.clientY, rect);
      updateWireDraft(graphPos.x, graphPos.y);
      return;
    }

    // 3. Marquee selection update
    if (interaction.kind === 'marqueeSelecting' && marquee && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const graphPos = screenToGraph(e.clientX, e.clientY, rect);
      setMarquee((prev) => (prev ? { ...prev, current: graphPos } : null));

      setSelection(getMarqueeNodeIds(canonical.nodes, marquee.start, graphPos, marqueeBaseSelectionRef.current));
      return;
    }

    // 4. Node dragging update
    if (interaction.kind === 'draggingNodes') {
      const dx = (e.clientX - interaction.initialMouse.x) / zoom;
      const dy = (e.clientY - interaction.initialMouse.y) / zoom;

      const nextPreview: Record<NodeId, Position> = {};
      for (const [id, initialPos] of Object.entries(interaction.initialPositions)) {
        nextPreview[id] = {
          x: Math.round(initialPos.x + dx),
          y: Math.round(initialPos.y + dy),
        };
      }
      dragPreviewPositionsRef.current = nextPreview;
      setDragPreviewPositions(nextPreview);
    }
  };

  // Global Pointer Up on container
  const finishPointerInteraction = (commitNodeMove: boolean) => {
    const interaction = interactionRef.current;
    if (interaction.kind === 'marqueeSelecting') {
      setMarquee(null);
    }
    if (interaction.kind === 'draggingNodes') {
      const moves = Object.entries(interaction.initialPositions)
        .map(([nodeId, from]) => ({ nodeId, from, to: dragPreviewPositionsRef.current[nodeId] ?? from }))
        .filter((move) => move.from.x !== move.to.x || move.from.y !== move.to.y);
      if (commitNodeMove && moves.length > 0) dispatch(new MoveMultipleNodesCommand(moves, false));
      dragPreviewPositionsRef.current = {};
      setDragPreviewPositions({});
    }
    if (wireDraft) {
      clearWireDraft();
    }
    setPrimaryInteraction({ kind: 'idle' });
  };

  const handlePointerUp = () => finishPointerInteraction(true);
  const handlePointerCancel = cancelPointerInteraction;

  // Quick add node selection
  const handleCreateNodeFromSearch = (type: string) => {
    const instance = defaultNodeRegistry.createDefaultInstance(type, {
      x: quickSearch.graphX,
      y: quickSearch.graphY,
    });
    if (instance) {
      dispatch(new CreateNodeCommand(instance));
    }
    setQuickSearch((prev) => ({ ...prev, open: false }));
  };

  const handleToggleCollapse = useCallback(
    (nodeId: NodeId) => dispatch(new ToggleNodeCollapseCommand(nodeId)),
    [dispatch]
  );

  // Filtered node definitions for quick search
  const filteredNodeDefs = defaultNodeRegistry.getAll().filter((def) => {
    if (!quickSearch.filterText) return true;
    const q = quickSearch.filterText.toLowerCase();
    return (
      def.label.toLowerCase().includes(q) ||
      def.category.toLowerCase().includes(q) ||
      def.description.toLowerCase().includes(q) ||
      def.keywords?.some((k) => k.toLowerCase().includes(q))
    );
  });

  return (
    <div
      ref={containerRef}
      data-workspace-viewport
      className={`${styles.viewportContainer} ${interactionKind === 'panning' ? styles.isPanning : ''}`}
      onWheel={handleWheel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onLostPointerCapture={handlePointerCancel}
      tabIndex={0}
    >
      {/* Transformed infinite canvas plane */}
      <div
        className={styles.transformPlane}
        style={{
          transform: `translate3d(${x}px, ${y}px, 0) scale(${zoom})`,
        }}
      >
        {/* Render Frame / Group overlays behind nodes */}
        {canonical.frames &&
          Object.values(canonical.frames).map((frame) => (
            <div
              key={frame.id}
              className={styles.frameElement}
              style={{
                transform: `translate3d(${frame.bounds.x}px, ${frame.bounds.y}px, 0)`,
                width: `${frame.bounds.width}px`,
                height: `${frame.bounds.height}px`,
                borderColor: frame.color || undefined,
              }}
            >
              <div className={styles.frameTitle} style={{ color: frame.color || undefined }}>
                {frame.title}
              </div>
            </div>
          ))}

        {/* Rubber-band Marquee Selection Box */}
        {marquee && (
          <div
            className={styles.marqueeBox}
            style={{
              left: `${Math.min(marquee.start.x, marquee.current.x)}px`,
              top: `${Math.min(marquee.start.y, marquee.current.y)}px`,
              width: `${Math.abs(marquee.current.x - marquee.start.x)}px`,
              height: `${Math.abs(marquee.current.y - marquee.start.y)}px`,
            }}
          />
        )}

        {/* SVG Connection Layer */}
        <svg className={styles.svgLayer}>
          <g transform="translate(50000, 50000)">
            {/* Render all established canonical edges */}
            {Object.values(canonical.edges).map((edge) => {
              const srcKey = `${edge.sourceNodeId}:${edge.sourcePortId}`;
              const tgtKey = `${edge.targetNodeId}:${edge.targetPortId}`;
              const sourceNode = canonical.nodes[edge.sourceNodeId];
              const targetNode = canonical.nodes[edge.targetNodeId];
              const p1 = getNodePortPosition(sourceNode, dragPreviewPositions[edge.sourceNodeId], portCoords[srcKey], true);
              const p2 = getNodePortPosition(targetNode, dragPreviewPositions[edge.targetNodeId], portCoords[tgtKey], false);
              const portType = sourceNode?.outputs[edge.sourcePortId]?.type || 'any';

              return (
                <ConnectionWire
                  key={edge.id}
                  id={edge.id}
                  x1={p1.x}
                  y1={p1.y}
                  x2={p2.x}
                  y2={p2.y}
                  sourceType={portType}
                  selected={selectedEdgeIds.has(edge.id)}
                  onSelect={selectEdge}
                  onDelete={(id) => dispatch(new DisconnectEdgeCommand(id))}
                />
              );
            })}

            {/* In-flight wire draft */}
            {wireDraft && (
              <ConnectionDraft
                x1={wireDraft.startX}
                y1={wireDraft.startY}
                x2={wireDraft.currentX}
                y2={wireDraft.currentY}
                sourceType={wireDraft.sourceType}
              />
            )}
          </g>
        </svg>

        {/* DOM Node Layer */}
        <div className={styles.nodeLayer}>
          {Object.values(canonical.nodes).map((node) => {
            const def = defaultNodeRegistry.get(node.type);
            const category = def?.category || 'Custom';
            const previewPosition = dragPreviewPositions[node.id];
            const displayNode = previewPosition ? { ...node, position: previewPosition } : node;

            return (
              <NodeElement
                key={node.id}
                node={displayNode}
                category={category}
                selected={selectedNodeIds.has(node.id)}
                onSelect={selectNode}
                onDragStart={handleNodeDragStart}
                onPortPinMeasure={handlePortPinMeasure}
                onStartWire={handleStartWire}
                onEndWire={handleEndWire}
                onToggleCollapse={handleToggleCollapse}
              />
            );
          })}
        </div>
      </div>

      {/* Quick Node Search Palette at cursor */}
      {quickSearch.open && (
        <div
          className={styles.quickSearchModal}
          style={{ left: `${quickSearch.screenX}px`, top: `${quickSearch.screenY}px` }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            ref={searchInputRef}
            type="text"
            className={styles.quickSearchInput}
            placeholder="Search nodes (e.g. Blur, Levels, Resize)..."
            value={quickSearch.filterText}
            onChange={(e) => setQuickSearch((prev) => ({ ...prev, filterText: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && filteredNodeDefs.length > 0) {
                handleCreateNodeFromSearch(filteredNodeDefs[0].type);
              }
            }}
          />
          <div className={styles.quickSearchResults}>
            {filteredNodeDefs.map((def) => (
              <div
                key={def.type}
                className={styles.quickSearchItem}
                onClick={() => handleCreateNodeFromSearch(def.type)}
              >
                <span>{def.label}</span>
                <span className={styles.searchCategoryBadge}>{def.category}</span>
              </div>
            ))}
            {filteredNodeDefs.length === 0 && (
              <div style={{ padding: '12px', fontSize: '11px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
                No matching nodes found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
